/**
 * Utility for converting files to UTF-8 encoding, handling non-UTF-8 characters.
 * Based on Python convert_to_utf8.py implementation.
 */

type ConversionResult = {
  content: string;
  encoding: string | null;
  blob: Blob;
  fileName: string;
};

/**
 * Check if a file is a text type that should be converted
 */
export function isTextFile(mimeType: string, fileName: string): boolean {
  const textMimeTypes = [
    'text/',
    'application/json',
    'application/xml',
    'application/javascript',
    'application/x-javascript',
    'application/csv',
  ];

  if (textMimeTypes.some(type => mimeType.startsWith(type))) {
    return true;
  }

  // Check file extension as fallback
  const textExtensions = [
    '.txt',
    '.csv',
    '.json',
    '.xml',
    '.html',
    '.htm',
    '.js',
    '.ts',
    '.jsx',
    '.tsx',
    '.css',
    '.scss',
    '.sass',
    '.md',
    '.log',
    '.yaml',
    '.yml',
    '.sql',
    '.sh',
    '.bat',
  ];

  return textExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
}

/**
 * Convert a File object to UTF-8 encoding
 */
export async function convertToUtf8(file: File): Promise<ConversionResult> {
  // Read file as ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Try different encodings to decode the file
  // Note: TextDecoder uses 'windows-1252' for both 'cp1252' and 'windows-1252'
  // and 'iso-8859-1' for both 'latin-1' and 'iso-8859-1'
  const encodings = [
    { label: 'utf-8', decoder: 'utf-8' },
    { label: 'cp1252', decoder: 'windows-1252' },
    { label: 'windows-1252', decoder: 'windows-1252' },
    { label: 'latin-1', decoder: 'iso-8859-1' },
    { label: 'iso-8859-1', decoder: 'iso-8859-1' },
  ];
  let content: string | null = null;
  let detectedEncoding: string | null = null;

  for (const { label, decoder } of encodings) {
    try {
      const textDecoder = new TextDecoder(decoder, { fatal: true });
      const decoded = textDecoder.decode(arrayBuffer);

      // Verify the content doesn't have replacement characters
      if (!decoded.includes('\ufffd')) {
        content = decoded;
        detectedEncoding = label;
        break;
      }
    } catch {
      // Decoding failed, try next encoding
      continue;
    }
  }

  // If all encodings fail or have replacement chars, use ignore strategy
  if (content === null || content.includes('\ufffd')) {
    const decoder = new TextDecoder('utf-8', { fatal: false });
    content = decoder.decode(arrayBuffer);
    detectedEncoding = 'utf-8 (invalid bytes removed)';
  }

  // Create UTF-8 blob
  const utf8Blob = new Blob([content], { type: file.type || 'text/plain;charset=utf-8' });

  return {
    content,
    encoding: detectedEncoding,
    blob: utf8Blob,
    fileName: file.name,
  };
}

/**
 * Ensure file is UTF-8 encoded. Returns the original file if already UTF-8 or not a text file.
 */
export async function ensureUtf8(file: File): Promise<File> {
  // Only process text files
  if (!isTextFile(file.type, file.name)) {
    return file;
  }

  try {
    const result = await convertToUtf8(file);

    // If encoding was already UTF-8 (without fallback), return original file
    if (result.encoding === 'utf-8') {
      return file;
    }

    // Return new File with UTF-8 content
    return new File([result.blob], file.name, {
      type: file.type || 'text/plain;charset=utf-8',
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.error(`Failed to convert ${file.name} to UTF-8:`, error);
    // Return original file if conversion fails
    return file;
  }
}
